import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toMelbourneISODate } from '@/lib/melbourneTime';
import { useSubscription } from '@/hooks/useSubscription';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { 
  Wrench, 
  Users, 
  ClipboardList, 
  FileText, 
  TrendingUp,
  AlertCircle,
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Car,
  CalendarIcon,
  X,
  Mail,
  MessageSquare,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import MechanicLabourTarget from '@/components/MechanicLabourTarget';
import MechanicPerformanceBanner, { MechanicPerformanceCard } from '@/components/MechanicPerformanceBanner';
import MonthlyProductivitySummary from '@/components/dashboard/MonthlyProductivitySummary';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service type colors
const SERVICE_COLORS = {
  service: '#3B82F6',
  rwc: '#10B981',
  tyres: '#8B5CF6',
  aircon: '#06B6D4',
  other: '#6B7280'
};

const SERVICE_LABELS = {
  service: 'Service',
  rwc: 'RWC',
  tyres: 'Tyres',
  aircon: 'Aircon',
  other: 'Other'
};

// Bar Chart Component
function BookingsBarChart({ chartData, selectedBar, onBarSelect, view }) {
  if (!chartData || !chartData.stats) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const maxTotal = Math.max(...chartData.stats.map(d => d.total), 1);
  const serviceTypes = ['service', 'rwc', 'tyres', 'aircon', 'other'];
  const barCount = chartData.stats.length;

  return (
    <div className="space-y-3">
      {/* Bar Chart */}
      <div className="flex items-end justify-between gap-1 h-40 px-1">
        {chartData.stats.map((item) => {
          const isSelected = selectedBar === item.date;
          const barHeight = (item.total / maxTotal) * 100;
          
          return (
            <div 
              key={item.date}
              className={`flex-1 flex flex-col items-center cursor-pointer transition-all ${
                isSelected ? 'scale-105' : 'hover:scale-102'
              }`}
              onClick={() => onBarSelect(item)}
              style={{ maxWidth: barCount > 10 ? '50px' : '70px' }}
            >
              {/* Stacked Bar */}
              <div 
                className={`w-full rounded-t overflow-hidden transition-all ${
                  isSelected ? 'ring-2 ring-blue-500' : ''
                } ${item.is_current ? 'ring-2 ring-blue-300' : ''}`}
                style={{ height: `${Math.max(barHeight, 4)}%`, minHeight: '4px' }}
              >
                {item.total > 0 ? (
                  <div className="flex flex-col h-full">
                    {serviceTypes.map((type) => {
                      const count = item.by_service_type[type] || 0;
                      if (count === 0) return null;
                      const segmentHeight = (count / item.total) * 100;
                      return (
                        <div
                          key={type}
                          style={{ 
                            backgroundColor: SERVICE_COLORS[type],
                            height: `${segmentHeight}%`
                          }}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-full bg-slate-200 rounded-t"></div>
                )}
              </div>
              
              {/* Label */}
              <div className={`mt-1 text-center ${isSelected ? 'font-bold' : ''}`}>
                <p className={`text-xs ${item.is_current ? 'text-blue-600 font-semibold' : 'text-slate-500'}`}>
                  {item.label}
                </p>
                <p className={`text-xs font-medium ${isSelected ? 'text-blue-600' : 'text-slate-700'}`}>
                  {item.total}
                </p>
                <p className={`text-[10px] ${item.loan_car_count > 0 ? 'text-cyan-600' : 'text-slate-400'} flex items-center justify-center gap-0.5`}>
                  <Car className="h-2.5 w-2.5" />
                  {item.loan_car_count || 0}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3 pt-2 border-t">
        {serviceTypes.map((type) => (
          <div key={type} className="flex items-center gap-1 text-xs">
            <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: SERVICE_COLORS[type] }}></div>
            <span className="text-slate-600">{SERVICE_LABELS[type]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Selected Item Breakdown Component
function SelectedBreakdown({ item, view, onServiceTypeClick }) {
  if (!item) return null;

  const serviceTypes = ['service', 'rwc', 'tyres', 'aircon', 'other'];
  const maxCount = Math.max(...Object.values(item.by_service_type), 1);
  
  const getClickHint = () => {
    if (view === 'yearly') return 'Click again to see monthly breakdown';
    if (view === 'monthly') return 'Click again to see weekly breakdown';
    if (view === 'weekly') return 'Click a service type below to jump to its job-card Kanban';
    return null;
  };

  // Only enable service-type click drill-down at the daily granularity (weekly
  // view shows bars per day, so an item.date here = YYYY-MM-DD). For monthly /
  // yearly views the breakdown spans many days and the deep-link would be
  // ambiguous.
  const dailyDrillEnabled = view === 'weekly' && /^\d{4}-\d{2}-\d{2}$/.test(item.date || '');

  return (
    <div className="mt-4 pt-4 border-t" data-testid="dashboard-selected-breakdown">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm font-medium text-slate-700">{item.label}</span>
          {item.display_date && (
            <span className="text-xs text-slate-500 ml-2">({item.display_date})</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-blue-600">{item.total}</span>
            <span className="text-sm text-slate-500">bookings</span>
          </div>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${item.loan_car_count > 0 ? 'bg-cyan-50' : 'bg-slate-100'}`}>
            <Car className={`h-4 w-4 ${item.loan_car_count > 0 ? 'text-cyan-600' : 'text-slate-400'}`} />
            <span className={`text-sm font-bold ${item.loan_car_count > 0 ? 'text-cyan-600' : 'text-slate-400'}`}>{item.loan_car_count || 0}</span>
            <span className={`text-xs ${item.loan_car_count > 0 ? 'text-cyan-500' : 'text-slate-400'}`}>loan cars</span>
          </div>
        </div>
      </div>
      
      <div className="space-y-2">
        {serviceTypes.map((type) => {
          const count = item.by_service_type[type] || 0;
          const barWidth = (count / maxCount) * 100;
          const clickable = dailyDrillEnabled && count > 0;

          return (
            <div
              key={type}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              className={`flex items-center gap-2 rounded transition-colors ${
                clickable ? 'cursor-pointer hover:bg-blue-50 px-1 -mx-1' : ''
              }`}
              onClick={() => clickable && onServiceTypeClick && onServiceTypeClick(item.date, type)}
              onKeyDown={(e) => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onServiceTypeClick && onServiceTypeClick(item.date, type);
                }
              }}
              data-testid={`dash-breakdown-row-${type}`}
              data-clickable={clickable ? 'true' : 'false'}
            >
              <div className={`w-16 text-xs ${clickable ? 'text-blue-600 font-medium hover:underline' : 'text-slate-600'}`}>
                {SERVICE_LABELS[type]}
              </div>
              <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                <div 
                  className="h-full rounded flex items-center justify-end pr-1.5 transition-all duration-300"
                  style={{ 
                    backgroundColor: SERVICE_COLORS[type],
                    width: `${Math.max(barWidth, count > 0 ? 8 : 0)}%`
                  }}
                >
                  {count > 0 && <span className="text-white text-xs font-medium">{count}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {getClickHint() && (
        <p className="text-xs text-blue-600 mt-3 text-center">{getClickHint()}</p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const { hasFeature } = useSubscription();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [chartView, setChartView] = useState('weekly'); // weekly (default), monthly, yearly
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Date range filter
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showDateRange, setShowDateRange] = useState(false);
  
  // Navigation state for drill-down
  const [navState, setNavState] = useState(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      weekStart: toMelbourneISODate(monday)
    };
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchChartData();
    }
  }, [user, chartView, navState, dateRange]);

  // Live refresh: when any booking is created via the floating Workshop Pulse
  // widget (or anywhere else in the app), re-fetch the dashboard chart + stats
  // so the operator never has to reload the page to see the new count.
  useEffect(() => {
    if (!user) return undefined;
    const onBookingCreated = () => { fetchChartData(); };
    window.addEventListener('booking-created', onBookingCreated);
    return () => window.removeEventListener('booking-created', onBookingCreated);
     
  }, [user, chartView, navState, dateRange]);

  const fetchChartData = async () => {
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      let url = '';
      
      // If date range is active and both dates are set
      if (showDateRange && dateRange.from && dateRange.to) {
        // Use the bookings list endpoint with date filter for custom range
        const fromDate = format(dateRange.from, 'yyyy-MM-dd');
        const toDate = format(dateRange.to, 'yyyy-MM-dd');
        url = `${BACKEND_URL}/api/bookings/stats/range${siteParams}${sep}start_date=${fromDate}&end_date=${toDate}`;
      } else if (chartView === 'weekly') {
        // Show 7 days (Mon-Sun) of the week
        url = `${BACKEND_URL}/api/bookings/stats/weekly${siteParams}${sep}week_start=${navState.weekStart}`;
      } else if (chartView === 'monthly') {
        // Show weeks of selected month
        url = `${BACKEND_URL}/api/bookings/stats/monthly${siteParams}${sep}year=${navState.year}&month=${navState.month}`;
      } else if (chartView === 'yearly') {
        // Show 12 months of the year
        url = `${BACKEND_URL}/api/bookings/stats/yearly${siteParams}${sep}year=${navState.year}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setChartData(data);
        setSelectedItem(null);
        
        // Auto-select current period
        const current = data.stats?.find(s => s.is_current);
        if (current) {
          setSelectedItem(current);
        }
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    }
  };

  const handleBarSelect = (item) => {
    setSelectedItem(item);
    
    // Drill down on double-click (same item selected again)
    if (selectedItem?.date === item.date) {
      if (chartView === 'yearly' && item.month) {
        setNavState(prev => ({ ...prev, month: item.month }));
        setChartView('monthly');
      } else if (chartView === 'monthly' && item.week_start) {
        setNavState(prev => ({ ...prev, weekStart: item.week_start }));
        setChartView('weekly');
      }
    }
  };

  const handleBack = () => {
    if (chartView === 'weekly') {
      setChartView('monthly');
    } else if (chartView === 'monthly') {
      setChartView('yearly');
    }
    setSelectedItem(null);
  };

  const handleViewChange = (newView) => {
    const now = new Date();
    if (newView === 'weekly') {
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diff);
      setNavState(prev => ({ ...prev, weekStart: toMelbourneISODate(monday) }));
    } else if (newView === 'monthly') {
      setNavState(prev => ({ ...prev, month: now.getMonth() + 1 }));
    } else if (newView === 'yearly') {
      setNavState(prev => ({ ...prev, year: now.getFullYear() }));
    }
    setChartView(newView);
    setSelectedItem(null);
    setShowDateRange(false);
    setDateRange({ from: null, to: null });
  };

  const navigateWeek = (direction) => {
    const current = new Date(navState.weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setNavState(prev => ({ ...prev, weekStart: toMelbourneISODate(current) }));
    setSelectedItem(null);
  };

  const navigateMonth = (direction) => {
    let newMonth = navState.month + direction;
    let newYear = navState.year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setNavState(prev => ({ ...prev, month: newMonth, year: newYear }));
    setSelectedItem(null);
  };

  const navigateYear = (direction) => {
    setNavState(prev => ({ ...prev, year: prev.year + direction }));
    setSelectedItem(null);
  };

  const goToToday = () => {
    const now = new Date();
    if (chartView === 'weekly') {
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diff);
      setNavState(prev => ({ ...prev, weekStart: toMelbourneISODate(monday) }));
    } else if (chartView === 'monthly') {
      setNavState(prev => ({ ...prev, month: now.getMonth() + 1, year: now.getFullYear() }));
    } else if (chartView === 'yearly') {
      setNavState(prev => ({ ...prev, year: now.getFullYear() }));
    }
    setSelectedItem(null);
  };

  const clearDateRange = () => {
    setDateRange({ from: null, to: null });
    setShowDateRange(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const getRoleDisplay = (role) => {
    const roleMap = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      service_manager: 'Service Manager',
      floor_manager: 'Floor Manager',
      mechanic: 'Mechanic / Technician',
      parts_staff: 'Parts Staff'
    };
    return roleMap[role] || role;
  };

  const getFeatureCards = () => {
    const allCards = [
      { title: 'Bookings', description: 'View and manage bookings', icon: Calendar, roles: ['super_admin', 'admin', 'service_manager', 'floor_manager'], color: 'bg-orange-50 text-orange-600', path: '/bookings', sub_key: 'bookings' },
      { title: 'Job Cards', description: 'Manage workshop jobs', icon: ClipboardList, roles: ['super_admin', 'admin', 'service_manager', 'floor_manager', 'mechanic'], color: 'bg-blue-50 text-blue-600', path: '/job-cards', sub_key: 'job_cards' },
      { title: 'Customers', description: 'Customer information', icon: Users, roles: ['super_admin', 'admin', 'service_manager', 'floor_manager'], color: 'bg-emerald-50 text-emerald-600', path: '/customers', sub_key: 'customers' },
      { title: 'Comms', description: 'Send SMS & Email', icon: Send, roles: ['super_admin', 'admin', 'service_manager', 'floor_manager'], color: 'bg-pink-50 text-pink-600', path: '/comms', sub_key: 'comms' },
      { title: 'Invoices', description: 'Create and manage invoices', icon: FileText, roles: ['super_admin', 'admin', 'service_manager', 'floor_manager'], color: 'bg-amber-50 text-amber-600', path: '/invoices', sub_key: 'invoices' },
      { title: 'Loan Cars', description: 'Track loan vehicles', icon: Car, roles: ['super_admin', 'admin', 'service_manager', 'floor_manager'], color: 'bg-cyan-50 text-cyan-600', path: '/loan-cars', sub_key: 'loan_cars' },
      { title: 'Reports', description: 'Analytics and reports', icon: TrendingUp, roles: ['super_admin', 'admin'], color: 'bg-purple-50 text-purple-600', path: '/reports', sub_key: 'reports' },
      { title: 'Audit Logs', description: 'Activity logs', icon: AlertCircle, roles: ['super_admin'], color: 'bg-red-50 text-red-600', path: '/audit-logs' }
    ];
    // iter241 — quick-link tiles respect the workshop subscription tier too.
    return allCards.filter(card => card.roles.includes(user?.role) && (!card.sub_key || hasFeature(card.sub_key)));
  };

  const getChartTitle = () => {
    if (showDateRange && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'dd MMM')} - ${format(dateRange.to, 'dd MMM yyyy')}`;
    }
    return chartData?.title || 'Bookings';
  };

  const getNavigationLabel = () => {
    if (chartView === 'weekly') {
      const start = new Date(navState.weekStart);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${format(start, 'dd MMM')} - ${format(end, 'dd MMM yyyy')}`;
    } else if (chartView === 'monthly') {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return `${monthNames[navState.month - 1]} ${navState.year}`;
    } else {
      return navState.year.toString();
    }
  };

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="dashboard-container">
        {/* Mechanic Performance Banner - Shows for mechanics only */}
        <MechanicPerformanceBanner user={user} />
        
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="font-['Chivo'] font-bold text-3xl sm:text-4xl text-slate-900 mb-1">
              Welcome, {user?.name?.split(' ')[0] || 'User'}
            </h1>
            <p className="text-slate-500">
              {getRoleDisplay(user?.role)} • {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
          {/* "New Booking" button removed — Workshop Pulse floating widget
              provides the same shortcut on every page, so a duplicate button
              here was redundant. */}
        </div>

        {/* Bookings Chart — full width (Mechanic Labour Target removed per user request, productivity shown below + floating widget) */}
        <div className="mb-6">
          {/* Bookings Chart */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="bookings-chart-card">
            {/* Header with Title and Controls */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-lg text-slate-900">
                  {getChartTitle()}
                </h2>
                
                {/* View Selector */}
                <Select value={chartView} onValueChange={handleViewChange}>
                  <SelectTrigger className="w-28 h-9" data-testid="chart-view-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Navigation and Date Range Controls */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Navigation Controls */}
                {!showDateRange && (
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => {
                      if (chartView === 'weekly') navigateWeek(-1);
                      else if (chartView === 'monthly') navigateMonth(-1);
                      else navigateYear(-1);
                    }} data-testid="nav-prev">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="min-w-[140px] text-center">
                      <span className="text-sm font-medium text-slate-700">{getNavigationLabel()}</span>
                    </div>
                    <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => {
                      if (chartView === 'weekly') navigateWeek(1);
                      else if (chartView === 'monthly') navigateMonth(1);
                      else navigateYear(1);
                    }} data-testid="nav-next">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 px-3" onClick={goToToday} data-testid="nav-today">
                      Today
                    </Button>
                  </div>
                )}
                
                {/* Date Range Filter */}
                <div className="flex items-center gap-2 ml-auto">
                  {showDateRange && dateRange.from && dateRange.to ? (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                      <CalendarIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700">
                        {format(dateRange.from, 'dd MMM')} - {format(dateRange.to, 'dd MMM yyyy')}
                      </span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={clearDateRange}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-2" data-testid="date-range-btn">
                          <CalendarIcon className="h-4 w-4" />
                          Date Range
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <CalendarComponent
                          mode="range"
                          selected={dateRange}
                          onSelect={(range) => {
                            setDateRange(range || { from: null, to: null });
                            if (range?.from && range?.to) {
                              setShowDateRange(true);
                            }
                          }}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
            </div>
            
            <p className="text-xs text-slate-500 mb-3">
              {chartView === 'weekly' && 'Click a day to see breakdown. Click again to drill down.'}
              {chartView === 'monthly' && 'Click a week to see breakdown. Click again to drill down.'}
              {chartView === 'yearly' && 'Click a month to see breakdown. Click again to drill down.'}
              {showDateRange && dateRange.from && dateRange.to && 'Showing bookings for selected date range'}
            </p>
            
            <BookingsBarChart 
              chartData={chartData}
              selectedBar={selectedItem?.date}
              onBarSelect={handleBarSelect}
              view={chartView}
            />
            
            {selectedItem && (
              <SelectedBreakdown
                item={selectedItem}
                view={chartView}
                onServiceTypeClick={(date, serviceType) => {
                  // Navigate to JC kanban for that exact day + category
                  navigate(`/job-cards?date=${date}&service_type=${serviceType}`);
                }}
              />
            )}
          </Card>

          {/* Mechanic Performance Card (only for mechanics — Labour Target tile removed) */}
          {user && ['mechanic', 'technician', 'floor_manager'].includes(user.role) && (
            <div className="mt-4">
              <MechanicPerformanceCard user={user} />
            </div>
          )}
        </div>

        {/* Monthly Mechanic Productivity Summary (next to bookings, super_admin + service_manager + floor_manager) */}
        {user && ['super_admin', 'service_manager', 'floor_manager'].includes(user.role) && (
          <div className="mb-6" data-testid="dashboard-productivity-section">
            <MonthlyProductivitySummary user={user} />
          </div>
        )}

        {/* Feature Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {getFeatureCards().map((card, index) => {
            const Icon = card.icon;
            return (
              <Card
                key={index}
                data-testid={`feature-card-${card.title.toLowerCase().replace(' ', '-')}`}
                className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(card.path)}
              >
                <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-sm text-slate-900 mb-1">{card.title}</h3>
                <p className="text-slate-500 text-xs">{card.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Workshop Banner */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-200 rounded-lg p-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">AutoShopFlow Workshop Management</h3>
              <p className="text-slate-600 text-sm">Your complete digital workshop solution</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
